import React, { useEffect, useRef, useState } from 'react';
import { webLoadingFalse } from '../../services/navigate.service';
import {
  changeLockTransaction,
  getListTransactionMonth,
} from '../../services/transaction.service';
import { useResponsive } from '../../hook/useResponsive';
import { showApiError } from '../../services/apiError';
import LockIcon from '../../images/LockIcon';
import {
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  Pill,
  cells,
  t,
} from '../../components/list';

interface TransactionMonthRow {
  id: string;
  monthSequence: number;
  transactionYear: number;
  locked: boolean;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** "August, 2026" — computed client-side from monthSequence + transactionYear, as before. */
const monthLabelOf = (row: TransactionMonthRow, language: any) => {
  const name = MONTHS[Number(row?.monthSequence) - 1];
  return (name ? t(language, name, name) : String(row?.monthSequence ?? '')) + ', ' + row?.transactionYear;
};

const TransactionMonth = () => {
  const [language, setLanguage] = useState<any>(null);
  const { isMobile } = useResponsive();
  const listRef = useRef<ListScreenHandle>(null);
  const inforTable: any = {
    title:
      language && language?.['No Transaction Month Found']
        ? language?.['No Transaction Month Found']
        : 'No Transaction Month Found',
    path: '/transactionMonth/',
    desc:
      language && language?.['A short description about this.']
        ? language?.['A short description about this.']
        : 'A short description about this.',
  };

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  /** One page of months; the endpoint takes page + limit only (no search, no sort). */
  const fetchMonths = async (query: ListQuery) => {
    webLoadingFalse();
    const response: any = await getListTransactionMonth(String(query.page), query.limit);
    return {
      rows: response.data.data ?? [],
      count: response.data.count ?? 0,
    };
  };

  /** Lock / unlock a month, then refetch the page. */
  const toggleLock = async (row: TransactionMonthRow) => {
    try {
      await changeLockTransaction(String(row.id), { locked: !row.locked });
    } catch (error) {
      console.error('Error loading terms:', error);
      showApiError(error, { language });
    } finally {
      listRef.current?.reload();
    }
  };

  /* ---------------- List kit configuration ---------------- */

  const columns: ListColumn<TransactionMonthRow>[] = [
    {
      key: 'month',
      label: 'Transaction month',
      width: 260,
      hideable: false,
      value: (m, ctx) => monthLabelOf(m, ctx.language),
      render: cells.primary((m, ctx) => monthLabelOf(m, ctx.language)),
      mobile: 'title',
    },
    {
      key: 'locked',
      label: 'Status',
      width: 110,
      // Same colours as the legacy TagStatus: Unlocked = green, Locked = red.
      render: (m, ctx) =>
        m.locked ? (
          <Pill label={t(ctx.language, 'Locked', 'Locked')} kind="danger" />
        ) : (
          <Pill label={t(ctx.language, 'Unlocked', 'Unlocked')} kind="ok" />
        ),
      mobile: 'trailing',
    },
  ];

  const rowActions: ListRowAction<TransactionMonthRow>[] = [
    {
      key: 'lock',
      label: m => (m.locked ? t(language, 'Unlock', 'Unlock') : t(language, 'Lock', 'Lock')),
      icon: <LockToggleIcon />,
      sheetIcon: 'check',
      onRun: toggleLock,
    },
  ];

  return (
    <div
      className="h-full relative"
      style={{
        overflowY: isMobile ? 'auto' : undefined,
        paddingBottom: isMobile ? '24px' : undefined,
        display: isMobile ? undefined : 'flex',
        flexDirection: isMobile ? undefined : 'column',
      }}
    >
      <div className="flex items-center justify-between">
        <p className="font-inter" style={{ fontSize: '24px', fontWeight: '500', color: '#222525' }}>
          {' '}
          {language && language?.['Transaction month']
            ? language?.['Transaction month']
            : 'Transaction month'}
        </p>
      </div>
      <ListScreen
        ref={listRef}
        language={language}
        columns={columns}
        fetchRows={fetchMonths}
        search={false}
        rowActions={rowActions}
        countNoun="months"
        emptyState={inforTable}
      />
    </div>
  );
};

/**
 * The kebab icon is one node per action, not per row; the lock glyph stands
 * for the toggle either way (the label says Lock / Unlock).
 */
const LockToggleIcon = () => (
  <span style={{ display: 'inline-flex', width: '18px', justifyContent: 'center' }}>
    <LockIcon />
  </span>
);

export default TransactionMonth;
